import { TrashIcon } from '@phosphor-icons/react';
import { ColumnDef } from '@tanstack/react-table';
import { keyBy, mapValues } from 'lodash';
import { useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import colors from 'tailwindcss/colors';

import { Button } from '@/components/ui/button';
import { FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Autocomplete, useApiQuery } from '@/modules/common';
import { PrinterType } from '@/modules/common/types';
import { EventPrinterStatus } from '@/modules/events/constants';
import { EventPrinter } from '@/modules/events/types';

export const usePrintersTab = () => {
  const [activeFocusFieldId, setActiveFocusFieldId] = useState<string>('');

  const { control, watch } = useFormContext();

  const { data: printers, isFetching } = useApiQuery<PrinterType[]>(
    '/printers',
    {
      queryKey: ['printers'],
      initialData: [],
    }
  );

  const formattedPrintersData = useMemo((): Record<string, PrinterType> => {
    if (!printers) return {};

    return mapValues(keyBy(printers, 'id'));
  }, [printers]);

  const fields = watch('printers');
  const { append, remove, update } = useFieldArray({
    control,
    name: 'printers',
  });

  const handlePrinterStatusChange = (
    index: number,
    status: EventPrinterStatus
  ) => {
    const newStatus =
      status === EventPrinterStatus.Enabled
        ? EventPrinterStatus.Disabled
        : EventPrinterStatus.Enabled;
    update(index, { ...fields[index], status: newStatus });
  };

  const changePrinter = (index: number, printerId: string) => {
    update(index, {
      ...fields[index],
      type: formattedPrintersData[printerId]?.type,
      printerId,
    });
  };

  const onAdd = (printer: EventPrinter) => {
    append({
      ...printer,
      type: formattedPrintersData[printer.printerId].type,
    });
  };

  const onEdit = (printer: EventPrinter, selectedPrinter: EventPrinter) => {
    const index = fields.findIndex(
      ({ printerId }) => printerId === selectedPrinter.printerId
    );
    if (index !== -1) {
      update(index, {
        ...printer,
        type: formattedPrintersData[printer.printerId].type,
      });
    }
  };

  const columns: ColumnDef<EventPrinter>[] = [
    {
      size: 300,
      accessorKey: 'printerId',
      header: 'Printer',
      cell: ({ row }) => {
        return (
          <Autocomplete
            name={`printers.${row.index}.printerId`}
            control={control}
            placeholder="Select printer"
            options={printers}
            className="w-[200px] border-0"
            onChange={(value) => changePrinter(row.index, value)}
            defaultValue={
              formattedPrintersData[row.getValue('printerId') as string]
                ?.value ?? ''
            }
          />
        );
      },
    },
    {
      size: 200,
      accessorKey: 'name',
      header: 'Printer Name',
      cell: ({ row }) => {
        const fieldName = `printers.${row.index}.name`;

        return (
          <FormField
            control={control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <Input
                  {...field}
                  autoFocus={fieldName === activeFocusFieldId}
                  onFocus={() => setActiveFocusFieldId(fieldName)}
                  onBlur={() => setActiveFocusFieldId('')}
                  placeholder="Add name"
                  className="w-[140px] border-0"
                />
                <FormMessage />
              </FormItem>
            )}
          />
        );
      },
    },
    {
      size: 200,
      accessorKey: 'type',
      header: 'Type',
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.getValue('type')}</span>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.original.status;

        return (
          <label
            htmlFor={`printers.${row.index}.status`}
            className="cursor-pointer flex items-center gap-2 text-zinc-100"
          >
            <Switch
              id={`printers.${row.index}.status`}
              checked={status === EventPrinterStatus.Enabled}
              onCheckedChange={() =>
                handlePrinterStatusChange(row.index, status)
              }
            />
            <span className="text-medium">
              {status === EventPrinterStatus.Enabled ? 'Enable' : 'Disable'}
            </span>
          </label>
        );
      },
    },
    {
      size: 40,
      id: 'actions',
      header: 'Action',
      enableHiding: false,
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          data-testid="remove-printer-button"
          onClick={() => remove(row.index)}
        >
          <TrashIcon size={28} color={colors.zinc[400]} />
        </Button>
      ),
    },
  ];

  return {
    columns,
    fields,
    printers,
    isFetching,
    onAdd,
    onEdit,
  };
};
